import { Upload, Trash2, Check } from 'lucide-react';
import { COVER_MATERIALS } from './constants';

const resolveImg = (u) => {
  if (!u) return '';
  if (u.startsWith('http') || u.startsWith('data:')) return u;
  if (u.startsWith('/')) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
};

export function CoverEditor({
  coverImage, setCoverImage, coverText, setCoverText,
  coverMaterial, setCoverMaterial,
  coverColor, setCoverColor,
  coverInputRef, handleCoverUpload, covers,
}) {
  const materials = (covers && covers.length > 0) ? covers : COVER_MATERIALS;
  const selectedMaterial = materials.find(m => m.id === coverMaterial) || materials[0];
  const colors = selectedMaterial?.colors || [];

  return (
    <>
      <div className="bg-white rounded-xl border overflow-hidden" data-testid="cover-editor">
        <div className="px-4 py-3 border-b bg-slate-50">
          <h3 className="text-sm font-semibold text-slate-700">Designa albumets omslag</h3>
          <p className="text-xs text-slate-400">Ladda upp en bild och lägg till text på framsidan</p>
        </div>
        <div
          className="aspect-[3/4] bg-gradient-to-b from-slate-100 to-slate-50 flex items-center justify-center relative"
          style={coverColor?.hex ? { backgroundColor: coverColor.hex } : undefined}
        >
          <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} />
          {coverImage ? (
            <div className="relative w-full h-full group">
              <img src={coverImage} alt="Omslag" className="w-full h-full object-cover" />
              {coverText && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-6 py-8">
                  <p className="text-white text-2xl font-bold text-center drop-shadow-lg">{coverText}</p>
                </div>
              )}
              <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => coverInputRef.current?.click()} className="p-2 bg-white/90 text-slate-700 rounded-lg shadow-md hover:bg-white transition-colors" data-testid="change-cover-image">
                  <Upload className="w-4 h-4" />
                </button>
                <button onClick={() => setCoverImage(null)} className="p-2 bg-red-500/90 text-white rounded-lg shadow-md hover:bg-red-600 transition-colors" data-testid="remove-cover-image">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            <div onClick={() => coverInputRef.current?.click()} className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-slate-100/40 transition-colors" data-testid="cover-upload-area">
              <div className="w-20 h-20 rounded-full bg-[#2a9d8f]/10 flex items-center justify-center mb-4">
                <Upload className="w-8 h-8 text-[#2a9d8f]" />
              </div>
              <p className="text-base font-semibold text-slate-700">Ladda upp omslagsbild</p>
              <p className="text-sm text-slate-400 mt-1">Klicka för att välja en bild</p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border p-4" data-testid="cover-text-section">
        <h3 className="text-sm font-semibold text-slate-700 mb-2">Text på omslaget (valfritt)</h3>
        <input
          type="text"
          value={coverText}
          onChange={(e) => setCoverText(e.target.value)}
          placeholder="T.ex. Familjen Andersson 2026"
          maxLength={50}
          className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2a9d8f]"
          data-testid="cover-text-input"
        />
        <p className="text-xs text-slate-400 mt-1">Visas längst ner på omslaget</p>
      </div>

      <div className="bg-white rounded-xl border p-4" data-testid="cover-material-section">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Välj omslagsmaterial</h3>
        <div className="space-y-2">
          {materials.map(mat => {
            const isSelected = coverMaterial === mat.id;
            return (
              <button
                key={mat.id}
                onClick={() => { setCoverMaterial(mat.id); if (setCoverColor) setCoverColor(null); }}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 text-left transition-all ${
                  isSelected ? 'border-[#2a9d8f] bg-[#2a9d8f]/5' : 'border-slate-200 hover:border-slate-300'
                }`}
                data-testid={`cover-material-${mat.id}`}
              >
                {mat.image ? (
                  <img src={resolveImg(mat.image)} alt={mat.label} className="w-12 h-12 rounded-lg object-cover border border-slate-200 flex-shrink-0" />
                ) : (() => {
                  const styles = {
                    hardpaper: 'bg-amber-100 border border-amber-200',
                    fabric: 'bg-slate-200 border border-slate-300',
                  };
                  return <div className={`w-12 h-12 rounded-lg flex-shrink-0 ${styles[mat.id] || 'bg-stone-800 border border-stone-700'}`} />;
                })()}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800">{mat.label}</p>
                  <p className="text-xs text-slate-400">{mat.desc}</p>
                </div>
                <span className="text-sm font-semibold text-slate-600 flex-shrink-0">
                  {mat.price === 0 ? 'Ingår' : `+${mat.price} kr`}
                </span>
              </button>
            );
          })}
        </div>

        {/* Color picker for the selected material (only if admin defined colors) */}
        {colors.length > 0 && setCoverColor && (
          <div className="mt-4 pt-4 border-t border-slate-100" data-testid="cover-color-section">
            <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Välj färg</h4>
            <div className="flex flex-wrap gap-2">
              {colors.map((color, idx) => {
                const isActive = coverColor?.name === color.name;
                return (
                  <button
                    key={`${color.name}-${idx}`}
                    onClick={() => setCoverColor(color)}
                    className="flex items-center gap-1.5 px-2 py-1.5 rounded-full border-2 transition-all"
                    style={{
                      borderColor: isActive ? '#2a9d8f' : '#e2e8f0',
                      backgroundColor: isActive ? 'rgba(42,157,143,0.05)' : 'white',
                    }}
                    data-testid={`cover-color-${color.name}`}
                    title={color.name}
                  >
                    <span
                      className="w-5 h-5 rounded-full border border-slate-300 flex items-center justify-center"
                      style={{ backgroundColor: color.hex }}
                    >
                      {isActive && <Check className="w-3 h-3 text-white drop-shadow" />}
                    </span>
                    <span className="text-xs text-slate-700">{color.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
